import type { ReactNode } from "react"
import { cn } from "@/lib/utils.js"

/**
 * A screen opens with a title and nothing else. The panel does not narrate
 * itself: what a screen is for has to be evident from what it shows, and the
 * one caveat a screen genuinely owes its reader is rendered next to the thing
 * it concerns, not as a lecture under the title.
 */
export function PageHeader({
  title,
  kicker,
  titleClassName,
  actions,
}: {
  title: string
  /** A quiet word above the title saying what kind of thing this is. */
  kicker?: string
  titleClassName?: string
  actions?: ReactNode
}) {
  return (
    <header className="mb-7 flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
      <div>
        {kicker !== undefined && <p className="text-[13px] text-muted-foreground">{kicker}</p>}
        <h2
          className={cn("text-[22px] font-semibold tracking-tight text-foreground", titleClassName)}
        >
          {title}
        </h2>
      </div>
      {actions !== undefined && <div className="flex items-center gap-2">{actions}</div>}
    </header>
  )
}

/**
 * A figure: a plain label over a number. Two sizes — `lg` for the numbers a
 * screen leads with, `md` for a source's facts. The sentence that used to sit
 * under every number is a tooltip on the label; captions under every figure
 * are what a generated dashboard looks like, and after the first day nobody
 * reads them.
 *
 * `tone` tints the value only, and only when it deserves attention. Big
 * figures are set in the text face — mono is reserved for identifiers.
 */
export function Stat({
  label,
  value,
  hint,
  detail,
  tone = "neutral",
  size = "lg",
}: {
  label: string
  value: ReactNode
  hint?: string
  /**
   * One short line under the value — what it is being compared with, never an
   * explanation of what it is. That stays in `hint`.
   */
  detail?: ReactNode
  tone?: "neutral" | "ember" | "allow" | "prepay" | "critical"
  size?: "lg" | "md"
}) {
  const tones = {
    neutral: "text-foreground",
    ember: "text-primary",
    allow: "text-allow",
    prepay: "text-prepay",
    critical: "text-critical",
  } as const

  return (
    <div {...(hint === undefined ? {} : { title: hint })}>
      <p
        className={cn(
          "text-[13px] text-muted-foreground",
          hint !== undefined &&
            "cursor-help underline decoration-border decoration-dotted underline-offset-4",
        )}
      >
        {label}
      </p>
      <p
        className={cn(
          "font-semibold tracking-tight",
          size === "lg" ? "mt-1 text-[22px] leading-7" : "mt-0.5 text-[17px] leading-6",
          tones[tone],
        )}
      >
        {value}
      </p>
      {detail !== undefined && (
        <p className="mt-0.5 text-[12.5px] text-muted-foreground">{detail}</p>
      )}
    </div>
  )
}

/**
 * The scoreboard: one white surface holding a screen's figures side by side.
 * One object, not a grid of tiles — the difference between an instrument and
 * a dashboard template.
 */
export function StatRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end gap-x-12 gap-y-5 rounded-lg border bg-card px-5 py-4 shadow-xs">
      {children}
    </div>
  )
}

/** A titled block inside a screen. Sections and whitespace, not nested boxes. */
export function Section({
  title,
  hint,
  actions,
  children,
}: {
  title: string
  hint?: string
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
          {hint !== undefined && <p className="text-[13px] text-muted-foreground">{hint}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  )
}

/**
 * The stamp: a short vertical bar in the state's colour beside plain text.
 * The panel's one signature motif — a verdict, an active override, a failing
 * source. Used sparingly, so it marks rather than decorates.
 */
export function Notice({
  tone,
  children,
}: {
  tone: "review" | "critical" | "neutral"
  children: ReactNode
}) {
  const bars = {
    review: "border-review",
    critical: "border-critical",
    neutral: "border-border",
  } as const

  return (
    <div className={cn("border-l-[3px] py-0.5 pl-4 text-sm text-foreground", bars[tone])}>
      {children}
    </div>
  )
}
